import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { Stethoscope, CheckCircle2, LifeBuoy, Sparkles, ShieldCheck } from "lucide-react";
import { VoiceButton } from "@/components/VoiceButton";
import { AskAttachments } from "@/components/AskAttachments";
import { InstallBanner } from "@/components/InstallBanner";
import { API } from "@/lib/api";
import { useMemberCheck } from "@/lib/useMemberCheck";
import { getRememberedEmail } from "@/lib/memberEmail";

const ISSUES = ["A/V", "CCTV / Security", "Networking / Wi-Fi", "Smart Home", "Installation quote", "Other"];
const URGENCY = [
  { id: "normal", label: "Normal" },
  { id: "high", label: "High" },
  { id: "urgent", label: "Urgent" },
];

export default function AskDrGadget() {
  const [form, setForm] = useState({
    name: "", email: getRememberedEmail(), phone: "",
    issue_type: "A/V", urgency: "normal",
    description: "", preferred_contact: "email",
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const fromChat = useLocation().state?.fromChat;
  const memberInfo = useMemberCheck(form.email);
  const search = new URLSearchParams(useLocation().search);
  const wantsQuote = search.get("type") === "install";
  const project = search.get("project");
  useEffect(() => {
    if (fromChat) {
      setForm((f) => ({ ...f, issue_type: fromChat.issue_type, description: fromChat.description }));
      toast.success("We filled in the details from your chat — add your name and email to send it.");
    } else if (wantsQuote) {
      const lead = project ? `I'd like something like your "${project}" install.\n` : "I'd like a quote for an in-home installation.\n";
      setForm((f) => ({ ...f, issue_type: "Installation quote", description: f.description || `${lead}What: \nAddress / area: \nBest days & times: ` }));
    }
  }, [fromChat, wantsQuote, project]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.description) {
      toast.error("Please fill your name, email and describe the issue.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      const r = await axios.post(`${API}/tickets`, fd, { withCredentials: true });
      setDone(r.data);
      toast.success("Ticket created — tracking link sent to your email.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ticket submission failed");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
        <div className="dg-card rounded-2xl p-8 text-center dg-reveal">
          <div className="w-14 h-14 rounded-full bg-cyan-500/10 border border-cyan-500/40 grid place-items-center mx-auto">
            <CheckCircle2 className="w-7 h-7 text-cyan-400" />
          </div>
          <h1 className="font-display text-2xl font-bold mt-4">Ticket created</h1>
          <p className="text-slate-300 mt-2">Your ticket <span className="font-mono text-cyan-300">{done.ticket_id}</span> is with David.</p>
          {done.ai_suggestion && (
            <div className="mt-5 text-left rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4" data-testid="ai-suggestion">
              <div className="dg-eyebrow mb-2 flex items-center gap-2"><Sparkles className="w-3 h-3" /> While you wait — things to try</div>
              <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{done.ai_suggestion}</p>
            </div>
          )}
          <p className="text-slate-400 text-sm mt-2">Track anytime — no login needed:</p>
          <Link to={`/ticket/${done.tracking_token}`} className="dg-link block mt-2 break-all">{done.tracking_url}</Link>
          <div className="mt-6 flex justify-center gap-3">
            <Link to={`/ticket/${done.tracking_token}`} className="dg-btn-primary rounded-full px-5 py-2 text-sm" data-testid="go-track-btn">Open tracker</Link>
            <Link to="/" className="dg-btn-ghost rounded-full px-5 py-2 text-sm">Back home</Link>
          </div>
          <InstallBanner variant="done" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <div className="mb-8 dg-reveal">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/40 grid place-items-center"><Stethoscope className="w-5 h-5 text-cyan-400" /></div>
          <span className="dg-eyebrow">Support · Free in v1</span>
        </div>
        <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold font-display tracking-tight">Ask Dr.Gadget.</h1>
        <p className="mt-3 text-slate-300 max-w-2xl">Existing client or first-timer — describe the issue, drop a photo/video, and David will get back to you. Great for quick diagnostics before booking a phone or video session — or pick "Installation quote" for TV mounting, cameras or audio setups in the LA area.</p>
      </div>

      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
        <label className="block"><div className="dg-eyebrow mb-1">Your name</div>
          <input className="dg-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ask-name" required /></label>
        <label className="block"><div className="dg-eyebrow mb-1">Email</div>
          <input className="dg-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ask-email" required /></label>
        <div className="sm:col-span-2 text-xs" data-testid="ask-member-line">
          {memberInfo?.member
            ? <span className="dg-chip text-[#FFD600] border-[#FFD600]/50"><ShieldCheck className="w-3 h-3" /> {memberInfo.tier_name} member — your ticket goes to the front of the line</span>
            : <span className="text-slate-500">Members get priority answers — <Link to="/membership" className="dg-link">Dr.Gadget Care from $19/mo</Link>.</span>}
        </div>
        <label className="block"><div className="dg-eyebrow mb-1">Phone (optional)</div>
          <input className="dg-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ask-phone" /></label>
        <label className="block"><div className="dg-eyebrow mb-1">Preferred contact</div>
          <select className="dg-input" value={form.preferred_contact} onChange={(e) => setForm({ ...form, preferred_contact: e.target.value })} data-testid="ask-preferred">
            <option value="email">Email</option><option value="phone">Phone</option><option value="text">Text (SMS)</option>
          </select></label>
        <label className="block"><div className="dg-eyebrow mb-1">Issue type</div>
          <select className="dg-input" value={form.issue_type} onChange={(e) => setForm({ ...form, issue_type: e.target.value })} data-testid="ask-issue">
            {ISSUES.map((i) => <option key={i} value={i}>{i}</option>)}
          </select></label>
        <div className="block"><div className="dg-eyebrow mb-1">Urgency</div>
          <div className="grid grid-cols-3 sm:flex gap-2">
            {URGENCY.map((u) => (
              <button key={u.id} type="button" onClick={() => setForm({ ...form, urgency: u.id })} data-testid={`urg-${u.id}`}
                className={`px-4 py-2.5 min-h-[44px] rounded-full text-xs font-mono uppercase tracking-widest border transition-colors ${
                  form.urgency === u.id ? "bg-cyan-500 text-slate-900 border-cyan-500" : "bg-slate-900/50 text-slate-300 border-slate-700 hover:border-cyan-400"
                }`}>{u.label}</button>
            ))}
          </div></div>
        <label className="block sm:col-span-2"><div className="dg-eyebrow mb-1 flex items-center justify-between"><span>Describe the issue</span><VoiceButton testId="ask-voice-btn" onText={(t) => setForm((f) => ({ ...f, description: (f.description ? f.description + " " : "") + t }))} /></div>
          <textarea className="dg-input" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ask-desc" required placeholder="What's happening? Any error messages, blinking lights, what changed recently…" /></label>
        <div className="sm:col-span-2">
          <AskAttachments files={files} setFiles={setFiles} />
        </div>
        <div className="sm:col-span-2 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 mt-2">
          <div className="text-xs text-slate-500">By submitting, you agree we'll email you a tracking link and status updates.</div>
          <button type="submit" disabled={submitting} className="dg-btn-primary rounded-full px-6 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50 whitespace-nowrap w-full sm:w-auto" data-testid="ask-submit">
            <LifeBuoy className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit ticket"}
          </button>
        </div>
      </form>
      <InstallBanner />
    </div>
  );
}
